/**
 * Focused search policy.
 *
 * Narrows listings, counts and pins to the visible map area when the user zooms in,
 * without changing the location: the location type, URL path and H1 stay the same
 * and the URL only gains minlat/maxlat/minlng/maxlng.
 *
 * Applies when:
 *  - the location type is not a rectangle and the result type is not Builders;
 *  - the zoom is at least FOCUSED_SEARCH_ZOOM_THRESHOLD; and
 *  - focused mode is already on, or a real user gesture produced this position.
 *    A programmatic camera move (fitBounds, recenter, default zoom) never enters it.
 *
 * Decision: `updateFocused(searchBounds)` when already focused, otherwise
 * `enterFocused(searchBounds)`. Below the threshold it declines and the core's
 * neutral fallback ends focused mode, so the URL returns to the plain location.
 *
 * Events:
 *  - `start`: restores a deep link. When the page loads with
 *    minlat/maxlat/minlng/maxlng, the map is fitted to those bounds and zoomed to the
 *    threshold on the first idle, with a granted gesture so focused mode enters there.
 *  - `locationSelected`: ends focused mode for the new location.
 *  - `resultTypeChanged` to Builders: ends focused mode; builder results are not
 *    narrowed by the map.
 *
 * It runs after the search-within-map policies, so a rectangle or a pan far away wins
 * over narrowing the current location.
 *
 * Pure — no store, map, DOM or RxJS access.
 *
 * @module searchResultsMap/services/focusedSearch.policy
 */

import {
  BUILDERS,
  FOCUSED_SEARCH_ZOOM_THRESHOLD,
  RECTANGULAR_AREA,
} from '../constants';
import ViewportSearchMode from '../enums/viewportSearchMode';
import ViewportSearchEvent from '../enums/viewportSearchEvent';
import {
  deferUntilIdle,
  dispatchDecision,
  enterFocused,
  exitFocused,
  fitBounds,
  grantGesture,
  setZoom,
  updateFocused,
} from './decisions';

function getDeepLinkBounds(location) {
  const bounds = {
    minLatitude: parseFloat(location?.minimumLatitude),
    maxLatitude: parseFloat(location?.maximumLatitude),
    minLongitude: parseFloat(location?.minimumLongitude),
    maxLongitude: parseFloat(location?.maximumLongitude),
  };

  return Object.values(bounds).every(Number.isFinite) ? bounds : null;
}

function handleStart({ location, locationType }) {
  if (locationType === RECTANGULAR_AREA) {
    return [];
  }

  const bounds = getDeepLinkBounds(location);
  if (!bounds) {
    return [];
  }

  return [deferUntilIdle([grantGesture(), fitBounds(bounds), setZoom(FOCUSED_SEARCH_ZOOM_THRESHOLD)])];
}

export default function createFocusedSearchPolicy() {
  function evaluate(snapshot) {
    if (snapshot.locationType === RECTANGULAR_AREA || snapshot.resultType === BUILDERS) {
      return null;
    }

    if (snapshot.zoom < FOCUSED_SEARCH_ZOOM_THRESHOLD) {
      return null;
    }

    if (snapshot.mode === ViewportSearchMode.Focused) {
      return updateFocused(snapshot.searchBounds);
    }

    if (snapshot.hasUserGesture) {
      return enterFocused(snapshot.searchBounds);
    }

    return null;
  }

  function onEvent(event) {
    switch (event.type) {
      case ViewportSearchEvent.Start:
        return handleStart(event);
      case ViewportSearchEvent.LocationSelected:
        return [dispatchDecision(exitFocused())];
      case ViewportSearchEvent.ResultTypeChanged:
        return event.to === BUILDERS ? [dispatchDecision(exitFocused())] : [];
      default:
        return [];
    }
  }

  return {
    name: 'focused-search',
    evaluate,
    onEvent,
  };
}
